import React, { useEffect, useState, useCallback } from "react";
import NotificationList from "../../page-components/Notification/NotificationsList";
import NotificationsTabs from "../../page-components/Notification/NotificationTabs";
import { Flex, Spin, message } from "antd";
import notificationsService from "../../services/notificationService";
import { debounce } from "lodash"; // Importing debounce function
import { useLocation, useNavigate } from "react-router-dom";
import { Input } from "antd";
import { Search } from "lucide-react";
import store from "../../redux/store";
import { updatePermissions } from "../../redux/slices/authSlice";
import { setUpdateList } from "../../redux/slices/newUpdateSlice";
import { fetchUnreadCountSuccess } from "../../redux/slices/notificationsSlice";
import commonService from "../../services/commonService";
const Notification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const categories = [
    { key: "urgent", label: "Urgent", color: "#f87171" },
    { key: "high_priority", label: "High Priority", color: "#fdba74" },
    { key: "feedbacks", label: "Feedbacks", color: "#93c5fd" },
    { key: "resolved", label: "Resolved", color: "#d8b4fe" },
    { key: "info", label: "Info", color: "#cbd5e1" },
  ];
  const [searchValue, setSearchValue] = useState("");
  const queryParams = new URLSearchParams(location.search);
  const level = queryParams.get("level");
  const searchQuery = queryParams.get("search") || ""; // Get search param
  const [activeTab, setActiveTab] = useState(level);
  const [isLoading, setIsLoading] = useState(false);
  // Initialize showUnread from URL param if present
  const unreadParam = queryParams.get("unread");
  const [showUnread, setShowUnread] = useState(unreadParam === "true");
  const [notifications, setNotifications] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [levelCounts, setLevelCounts] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: false,
  });

  useEffect(() => {
    // Set activeTab to the corresponding category key if level matches
    if (level && categories.some((category) => category.key === level)) {
      setActiveTab(level);
    } else {
      setActiveTab(categories[0].key); // Default to first category if no match
    }
    // Sync unread filter from URL param if present
    const unreadParam = queryParams.get("unread");
    if (unreadParam !== null) {
      setShowUnread(unreadParam === "true");
    }
  }, [location.search]);

  // Stable debounced notification fetcher
  const getNotifications = async (
    level = activeTab,
    search = "",
    page = 1,
    pageSize = 50,
    unread,
    type,
  ) => {
    try {
      setIsLoading(true);
      const adjustedLevel = search ? "all" : level;
      const response = await notificationsService.getNotificationsList(
        adjustedLevel,
        search,
        page,
        pageSize,
        unread,
        type,
      );
      if (response.success) {
        const fetchedNotifications = response?.data?.data.results.map(
          (notif) => ({
            verb: notif.html_verb,
            uid: notif.uid,
            unread: notif.unread,
            timestamp: notif.timestamp,
            type: notif.level,
            data: notif.data,
          }),
        );
        if (searchValue) {
          message.warning(
            "All search results are displayed, while the counter highlights only unread notifications.",
            10,
          );
        }
        setLevelCounts(response?.data?.data?.level_counts);
        setAvailableTypes(response?.data?.data?.available_types || []);
        setPagination((prev) => ({
          ...prev,
          total: response?.data?.data?.count || 0,
          current: page,
          pageSize,
        }));
        setIsLoading(false);
        setNotifications(fetchedNotifications);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Failed to fetch notifications:", error);
    }
  };

  // Stable debounced wrapper
  const debouncedGetNotifications = React.useRef(
    debounce((level, search, page, pageSize, unread, type) => {
      getNotifications(level, search, page, pageSize, unread, type);
    }, 500),
  ).current;

  const handleSearch = debounce(async (value) => {
    if (value.trim() !== "") {
      // ✅ Only fetch when there's input
      setSearchValue(value);
      await debouncedGetNotifications(
        "all",
        value,
        1,
        50,
        showUnread ? true : undefined,
        selectedType,
      );
    }
  }, 500);

  const handleMarkAsRead = async (uids) => {
    console.log("clicekdd");
    const response = await notificationsService.markAsReadNotification(uids);
    if (response.success) {
      setNotifications((prev) =>
        prev.map((notif) =>
          uids.includes(notif.uid) ? { ...notif, unread: false } : notif,
        ),
      );

      await debouncedGetNotifications(
        activeTab,
        "",
        pagination.current,
        pagination.pageSize,
        showUnread ? true : undefined,
        selectedType,
      );

      const updatedCounts = await commonService.getCommonInfo();
      if (updatedCounts.success) {
        console.log("updatedCounts", updatedCounts); // Dispatch action to save notifications data

        const { permissions, notifications, update_notifications } =
          updatedCounts.data.data;

        // Dispatch action to save permissions
        store.dispatch(updatePermissions(permissions));

        // Dispatch action to save notifications data
        store.dispatch(
          fetchUnreadCountSuccess({
            totalUnreadCount: notifications.total_unread_count,
            notificationsData: notifications.data,
          }),
        );

        // Dispatch action to save update notifications data
        store.dispatch(setUpdateList(update_notifications));
      }
      if (uids.length === 1) {
        message.success("notification marked as read.");
      }
      if (uids.length > 1) {
        message.success("notifications marked as read.");
      }
    } else {
      message.error(
        response.message || "Failed to mark notifications as read.",
      );
    }
  };
  const handleMarkAsUnread = async (uids) => {
    const response = await notificationsService.markAsUnreadNotification(uids);
    if (response.success) {
      setNotifications((prev) =>
        prev.map((notif) =>
          uids.includes(notif.uid) ? { ...notif, unread: true } : notif,
        ),
      );
      await debouncedGetNotifications(
        activeTab,
        "",
        pagination.current,
        pagination.pageSize,
        showUnread ? true : undefined,
        selectedType,
      );

      const updatedCounts = await commonService.getCommonInfo();
      if (updatedCounts.success) {
        console.log("updatedCounts", updatedCounts); // Dispatch action to save notifications data

        const { permissions, notifications, update_notifications } =
          updatedCounts.data.data;

        // Dispatch action to save permissions
        store.dispatch(updatePermissions(permissions));

        // Dispatch action to save notifications data
        store.dispatch(
          fetchUnreadCountSuccess({
            totalUnreadCount: notifications.total_unread_count,
            notificationsData: notifications.data,
          }),
        );

        // Dispatch action to save update notifications data
        store.dispatch(setUpdateList(update_notifications));
      }

      if (uids.length === 1) {
        message.success("notification marked as unread.");
      }
      if (uids.length > 1) {
        message.success("notifications marked as unread.");
      }
    } else {
      message.error(
        response.message || "Failed to mark notifications as unread.",
      );
    }
  };
  console.log(searchQuery, "search");
  useEffect(() => {
    if (!searchValue) {
      debouncedGetNotifications(
        activeTab,
        "",
        pagination.current,
        pagination.pageSize,
        showUnread ? true : undefined,
        selectedType,
      );
    }
    // eslint-disable-next-line
  }, [
    activeTab,
    pagination.current,
    pagination.pageSize,
    showUnread,
    selectedType,
  ]);

  const handleTypeChange = (value) => {
    const newType = value || null;
    setSelectedType(newType);
    setPagination((prev) => ({ ...prev, current: 1 }));
    debouncedGetNotifications(
      activeTab,
      "",
      1,
      pagination.pageSize,
      showUnread ? true : undefined,
      newType,
    );
  };

  const handleTabChange = (key) => {
    setIsLoading(true);
    setActiveTab(key);
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset pagination
    setSelectedType(null);
    setSearchValue("");

    // Update the URL search params
    const params = new URLSearchParams(location.search);
    params.set("level", key);
    params.delete("type");
    params.delete("search");
    if (showUnread) {
      params.set("unread", "true");
    } else {
      params.delete("unread");
    }
    navigate({ search: params.toString() }, { replace: true });
    // No direct notification fetch here; useEffect will handle it
  };

  // const handleTabChange = (key) => {
  //   setActiveTab(key); // Update active tab and fetch relevant notifications
  //   setPagination((prev) => ({ ...prev, current: 1 })); // Reset pagination to first page
  // };

  const handleShowUnreadToggle = () => {
    setShowUnread(!showUnread);
  };

  const handleNotificationClick = async (notification) => {
    const { uid, unread } = notification;

    // If the notification is unread, mark it as read
    if (unread) {
      await handleMarkAsRead([uid]);
    }
    if (!notification.data) {
      // Handle the case where data is null
      message.error("Notification data is unavailable.");
      return;
    }
    let redirectType = null;
    const { redirection, order_key } = notification.data;
    console.log(redirection, "re");
    if (redirection.includes("Comment")) {
      redirectType = "Comment";
    } else if (redirection.includes("Feedback")) {
      redirectType = "Feedback";
    } else if (redirection.includes("Solution")) {
      redirectType = "Solution";
    }
    // If it was already read, or we just marked it read, the list is already up to date
    // handleMarkAsRead handles the refresh if it was unread.
    // No need for extra fetch here.
    if (redirection.includes("Accept_Reject_Page")) {
      // window.location.href = `/order-management/my-orders/${order_key}`;
      window.open(
        `/order-management/order-operations/accept-reject-orders`,
        "_blank",
      );
      return;
    }

    if (redirection.includes("Transactions")) {
      // window.location.href = `/payment-operations/transactions`;
      window.open(`/payment-operations/transactions`, "_blank");
      return;
    }
    if (redirection.includes("Task_Detail")) {
      window.open(`/order-management/my-orders/${order_key}`, "_blank");
      return;
    }
    if (redirection.includes("Help_Center")) {
      window.open(`/help-center`, "_blank");
      return;
    }

    if (redirectType) {
      // window.location.href = `/order-management/my-orders/${order_key}?type=${redirectType}`;
      window.open(
        `/order-management/my-orders/${order_key}?type=${redirectType}`,
        "_blank",
      );
    } else {
      // window.location.href = `/order-management/my-orders/`;
    }
  };

  const filteredNotifications = notifications.filter((notif) => {
    if (activeTab === "all") return showUnread ? notif.unread : true;
    return (
      notif.type.toLowerCase() === activeTab &&
      (showUnread ? notif.unread : true)
    );
  });

  const filteredUnreadNotifications = async () => {
    const newShowUnread = !showUnread;
    setShowUnread(newShowUnread);
    // Update unread param in URL
    const params = new URLSearchParams(location.search);
    if (newShowUnread) {
      params.set("unread", "true");
    } else {
      params.delete("unread");
    }
    navigate({ search: params.toString() }, { replace: true });
    // No direct notification fetch here; useEffect will handle it
  };

  return (
    <Flex
      vertical
      align="flex-end"
      justify="flex-start"
      style={{
        width: "100%",
        margin: "0 auto",
        padding: "20px",
        background: "white",
      }}
    >
      <NotificationsTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onShowUnreadToggle={handleShowUnreadToggle}
        onMarkAllAsRead={async () => {
          const unreadIds = notifications
            .filter((n) => n.unread)
            .map((n) => n.uid);
          if (unreadIds.length > 0) {
            setIsLoading(true);
            const response =
              await notificationsService.markAsReadNotification(unreadIds);
            if (response.success) {
              message.success("All notifications marked as read.");
              await debouncedGetNotifications(
                activeTab,
                "",
                pagination.current,
                pagination.pageSize,
                showUnread ? true : undefined,
                selectedType,
              );
              const updatedCounts = await commonService.getCommonInfo();
              if (updatedCounts.success) {
                const { permissions, notifications, update_notifications } =
                  updatedCounts.data.data;
                store.dispatch(updatePermissions(permissions));
                store.dispatch(
                  fetchUnreadCountSuccess({
                    totalUnreadCount: notifications.total_unread_count,
                    notificationsData: notifications.data,
                  }),
                );
                store.dispatch(setUpdateList(update_notifications));
              }
            } else {
              setIsLoading(false);
              message.error(
                response.message || "Failed to mark notifications as read.",
              );
            }
          } else {
            message.info("No unread notifications to mark as read.");
          }
        }}
        onMarkSelectedAsRead={handleMarkAsRead}
        showUnread={showUnread}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        categories={categories}
        onSearch={handleSearch}
        levelCounts={levelCounts}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onMarkSelectedAsUnread={handleMarkAsUnread}
        filteredUnreadNotifications={filteredUnreadNotifications}
        availableTypes={availableTypes}
        selectedType={selectedType}
        onTypeChange={handleTypeChange}
      />

      {isLoading ? (
        <Flex style={{ width: "100%" }} justify="center">
          <Spin size="large" />
        </Flex>
      ) : (
        <NotificationList
          notifications={filteredNotifications}
          onMarkAsRead={handleMarkAsRead}
          onNotificationClick={handleNotificationClick}
          pagination={pagination}
          setPagination={setPagination}
          onMarkSelectedAsUnread={handleMarkAsUnread}
        />
      )}
    </Flex>
  );
};

export default Notification;
